// src/components/BottomBar.jsx
// Phones (3.51.0, the owner's pick of 2026-10-10): a bar fixed at the bottom
// of the screen, like the Gmail app: Overview · Compose · Outbox · Inbox ·
// More. Only the pages the person may open are shown. More slides up a sheet
// with every other page, grouped as in the left strip on computers. The
// Inbox shows how many faxes arrived since you last opened it; Outbox a red
// dot when one of your faxes failed; More a dot when a page in it needs you.
// It sets --cp-bottom-bar on the page so pop-ups (Toasts) sit above it.
import { useEffect, useRef } from 'react';
import Emo from './Emo';

// 3.51.0 (owner): the same order as the menu on computers
const MAIN = ['dashboard', 'compose', 'outgoing', 'incoming'];
const GROUP_ORDER = ['Faxes', 'People & settings', 'Settings', 'Activity', 'Super Admin', 'Resources'];
const orderOf = g => { const i = GROUP_ORDER.indexOf(g); return i < 0 ? GROUP_ORDER.length : i; };
export const BAR_HEIGHT = 62;

export default function BottomBar({ tabs, active, hrefFor, onGo, labelFor, badges = {}, alerts = {}, moreOpen, setMoreOpen }) {
  const main = MAIN.map(id => tabs.find(t => t.id === id)).filter(Boolean);
  const rest = tabs.filter(t => !MAIN.includes(t.id));
  const onMore = rest.some(t => t.id === active);
  const sheetRef = useRef(null);

  // Pop-ups and the page's last lines stay above the bar
  useEffect(() => {
    document.documentElement.style.setProperty('--cp-bottom-bar', `calc(${BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`);
    return () => document.documentElement.style.removeProperty('--cp-bottom-bar');
  }, []);
  // The sheet closes with Escape, and on opening a page
  useEffect(() => {
    if (!moreOpen) return undefined;
    const onKey = e => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('keydown', onKey);
    sheetRef.current?.querySelector('a')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [moreOpen, setMoreOpen]);
  useEffect(() => { setMoreOpen(false); }, [active, setMoreOpen]);

  const go = (e, id) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    setMoreOpen(false);
    onGo(id);
  };
  const groups = [];
  for (const t of rest) {
    const g = t.railGroup || 'Faxes';
    let x = groups.find(y => y.title === g);
    if (!x) { x = { title: g, items: [] }; groups.push(x); }
    x.items.push(t);
  }
  groups.sort((a, b) => orderOf(a.title) - orderOf(b.title));

  return (
    <>
      <div style={{ ...S.backdrop, ...(moreOpen ? S.backdropOn : {}) }} onClick={() => setMoreOpen(false)} aria-hidden="true" />
      <div ref={sheetRef} id="more-pages" role="dialog" aria-label="More pages" aria-hidden={!moreOpen}
        style={{ ...S.sheet, ...(moreOpen ? S.sheetOn : {}) }}>
        <div style={S.grip} aria-hidden="true" />
        {groups.map(g => (
          <div key={g.title} style={{ marginBottom: 10 }}>
            <div style={S.groupTitle}>{g.title}</div>
            <div style={S.grid}>
              {g.items.map(t => (
                <a key={t.id} href={hrefFor(t.id)} onClick={e => go(e, t.id)} tabIndex={moreOpen ? 0 : -1} data-tour={`tab-${t.id}`}
                  aria-current={active === t.id ? 'page' : undefined} style={{ ...S.tile, ...(active === t.id ? S.tileOn : {}) }}>
                  <span style={S.tileIcon}><Emo e={t.icon} /></span>
                  <span style={S.tileLabel}>{labelFor(t)}</span>
                  {alerts[t.id] && <span style={S.tileDot} title={alerts[t.id]} aria-label="needs attention" />}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>

      <nav aria-label="Main menu" style={S.bar}>
        {main.map(t => {
          const on = active === t.id;
          const count = t.id === 'incoming' ? badges.inbox : 0;
          return (
            <a key={t.id} href={hrefFor(t.id)} onClick={e => go(e, t.id)} aria-current={on ? 'page' : undefined} data-tour={`tab-${t.id}`}
              aria-label={`${labelFor(t, true)}${count ? `, ${count} new` : ''}${t.id === 'outgoing' && badges.outboxFailed ? ', a fax failed' : ''}`}
              style={{ ...S.item, ...(on ? S.itemOn : {}) }}>
              <span style={{ ...S.icon, ...(on ? S.iconOn : {}) }}>
                <Emo e={t.icon} />
                {count > 0 && <span style={S.count}>{count > 99 ? '99+' : count}</span>}
                {t.id === 'outgoing' && badges.outboxFailed && <span style={{ ...S.dot, background: 'var(--c-d32f2f)' }} />}
              </span>
              <span style={S.label}>{labelFor(t, true)}</span>
            </a>
          );
        })}
        {rest.length > 0 && (
          <button type="button" onClick={() => setMoreOpen(o => !o)} aria-expanded={moreOpen} aria-controls="more-pages" data-tour="tab-more"
            style={{ ...S.item, ...(onMore || moreOpen ? S.itemOn : {}) }}>
            <span style={{ ...S.icon, ...(onMore || moreOpen ? S.iconOn : {}) }}>
              <span aria-hidden="true" style={{ fontSize: 20, lineHeight: 1 }}>{moreOpen ? '✕' : '☰'}</span>
              {Object.keys(alerts).length > 0 && !moreOpen && <span style={S.dot} />}
            </span>
            <span style={S.label}>More</span>
          </button>
        )}
      </nav>
    </>
  );
}

const S = {
  bar: {
    position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 900, display: 'flex', alignItems: 'stretch',   // under pop-ups and panels (1000+)
    height: `calc(${BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`, paddingBottom: 'env(safe-area-inset-bottom, 0px)',
    paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)',
    background: 'var(--c-ffffff)', borderTop: '1px solid var(--b-c8e6c9)', boxShadow: '0 -4px 16px rgb(var(--c-000000-rgb) / 0.06)',
  },
  item: {
    flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
    textDecoration: 'none', color: 'var(--t-607d8b)', background: 'none', border: 'none', padding: '4px 2px', cursor: 'pointer',
    fontFamily: 'inherit', WebkitTapHighlightColor: 'transparent',
  },
  itemOn: { color: 'var(--t-00695c)' },
  icon: { position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 30, borderRadius: 999, fontSize: 19, transition: 'background .2s' },
  iconOn: { background: 'var(--c-e0f2f1)' },
  label: { fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' },
  count: {
    position: 'absolute', top: -3, right: 2, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, background: 'var(--c-d32f2f)',
    color: 'var(--t-ffffff)', fontSize: 10.5, fontWeight: 800, lineHeight: '18px', textAlign: 'center', boxShadow: '0 0 0 2px var(--c-ffffff)',
  },
  dot: { position: 'absolute', top: 2, right: 10, width: 9, height: 9, borderRadius: '50%', background: 'var(--c-ef6c00)', boxShadow: '0 0 0 2px var(--c-ffffff)' },
  backdrop: { position: 'fixed', inset: 0, zIndex: 898, background: 'rgb(var(--c-0f2823-rgb) / 0.3)', opacity: 0, pointerEvents: 'none', transition: 'opacity .25s' },
  backdropOn: { opacity: 1, pointerEvents: 'auto' },
  sheet: {
    position: 'fixed', left: 0, right: 0, bottom: `calc(${BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`, zIndex: 899,
    maxHeight: '70vh', overflowY: 'auto', overscrollBehavior: 'contain', padding: '8px 16px 14px',
    background: 'var(--c-ffffff)', borderRadius: '18px 18px 0 0', boxShadow: '0 -10px 30px rgb(var(--c-000000-rgb) / 0.18)',
    transform: 'translateY(110%)', visibility: 'hidden', transition: 'transform .28s cubic-bezier(.2,.8,.2,1), visibility 0s .28s',
  },
  sheetOn: { transform: 'none', visibility: 'visible', transition: 'transform .28s cubic-bezier(.2,.8,.2,1), visibility 0s' },
  grip: { width: 40, height: 4, borderRadius: 2, background: 'var(--c-cfd8dc)', margin: '2px auto 10px' },
  groupTitle: { fontSize: 11, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--t-78909c)', margin: '4px 2px 6px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 },
  tile: {
    position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '10px 4px', minHeight: 64,
    borderRadius: 12, textDecoration: 'none', color: 'var(--t-37474f)', background: 'var(--c-f5f7f8)', border: '1px solid var(--c-e0e0e0)',
  },
  tileOn: { color: 'var(--t-00695c)', background: 'var(--c-e0f2f1)', border: '1px solid var(--c-b2dfdb)' },
  tileIcon: { fontSize: 22, lineHeight: 1 },
  tileLabel: { fontSize: 12.5, fontWeight: 700, textAlign: 'center' },
  tileDot: { position: 'absolute', top: 6, right: 8, width: 8, height: 8, borderRadius: '50%', background: 'var(--c-ef6c00)' },
};
